import { config } from '../config';
import { auth } from '../config/firebase';

/**
 * Single-channel mic capture + Deepgram streaming for the onboarding wizard's
 * voice input. Modeled on the single-device path of
 * `frontend/src/services/audioCaptureService.ts` (its
 * `startSingleDeviceCapture` — see `getUserMedia` → `AudioContext` +
 * `createScriptProcessor` → linear16 frames over a raw WebSocket to
 * `deepgram_proxy_service.py`), but deliberately NOT a port of that file:
 * this is one person (the customer) talking to the AI, so there's no
 * customer/salesperson dual-channel split, no system-audio capture path, no
 * device picker, and no audio-level metering — all of which exist in the
 * original only to support two-party live-call recording. Connecting
 * without a `speaker` query param puts deepgram_proxy_service in its
 * "Training mode" branch, which streams transcripts straight back down this
 * same socket instead of fanning them out to session subscribers — exactly
 * the shape a single interactive caller wants.
 *
 * Every audio socket carries the signed-in user's Firebase ID token as
 * `&token=` (owner decision D7, 2026-10-05: the proxy refuses a tokenless
 * socket that is not a live call's creator stream, and this room is never
 * one). The token is fetched on each connect, never kept, so a capture started
 * an hour after sign-in still sends a valid one. Same parameter as both
 * desktop apps' audio sockets.
 */

export interface OnboardingTranscriptionResult {
  transcript: string;
  isFinal: boolean;
}

export type OnboardingAudioCaptureErrorReason =
  | 'permission-denied'
  | 'no-device'
  | 'connection-failed'
  | 'unknown';

export class OnboardingAudioCaptureError extends Error {
  reason: OnboardingAudioCaptureErrorReason;

  constructor(reason: OnboardingAudioCaptureErrorReason, message: string) {
    super(message);
    this.name = 'OnboardingAudioCaptureError';
    this.reason = reason;
  }
}

/** Convert Float32 PCM samples ([-1, 1]) to Int16 PCM (linear16) for Deepgram. */
function convertFloat32ToInt16(float32Array: Float32Array): Int16Array {
  const int16Array = new Int16Array(float32Array.length);
  for (let i = 0; i < float32Array.length; i++) {
    const clamped = Math.max(-1, Math.min(1, float32Array[i]));
    int16Array[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
  }
  return int16Array;
}

const DEEPGRAM_CONNECT_TIMEOUT_MS = 8000;

const SIGNED_OUT_MESSAGE = 'You are signed out. Sign in again to use voice input.';

/**
 * The proxy URL for one dictation capture: no `speaker` (the proxy's direct,
 * single-caller branch), the capture's own room, and the user's ID token.
 */
export function buildDictationProxyUrl(baseUrl: string, roomId: string, sampleRate: number, token: string): string {
  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}room_id=${encodeURIComponent(roomId)}&encoding=linear16`
    + `&sample_rate=${sampleRate}&channels=1&token=${encodeURIComponent(token)}`;
}

/** The signed-in user's Firebase ID token, refreshed by the SDK when near expiry; null when signed out. */
async function fetchAudioSocketToken(): Promise<string | null> {
  try {
    return (await auth.currentUser?.getIdToken()) ?? null;
  } catch (error) {
    console.error('[OnboardingAudioCapture] Could not get an ID token for the audio socket:', error);
    return null;
  }
}

export function parseOnboardingTranscriptionMessage(raw: string): OnboardingTranscriptionResult | null {
  try {
    const data = JSON.parse(raw);
    if (data?.type === 'transcript' && typeof data.text === 'string' && data.text.trim()) {
      return { transcript: data.text, isFinal: !!data.is_final || !!data.isFinal };
    }

    const transcript = data?.channel?.alternatives?.[0]?.transcript;
    if ((data?.type === 'Results' || data?.channel) && typeof transcript === 'string' && transcript.trim()) {
      return { transcript, isFinal: !!data.is_final || !!data.isFinal };
    }
  } catch {
    // Non-JSON / control frame — ignore.
  }
  return null;
}

class OnboardingAudioCaptureService {
  private mediaStream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private processor: ScriptProcessorNode | null = null;
  private deepgramSocket: WebSocket | null = null;
  private capturing = false;
  /** Set at the start of stopCapture() so the socket's own onclose handler
   *  can tell a deliberate stop apart from an unexpected drop — only the
   *  latter should tear down hardware reactively and notify the caller. */
  private manualStop = false;
  private onTranscriptionCallback: ((result: OnboardingTranscriptionResult) => void) | null = null;
  private onConnectionLostCallback: (() => void) | null = null;

  /** Notified if the Deepgram connection drops mid-capture (network blip,
   *  proxy restart) — NOT called on a deliberate stopCapture(). The caller
   *  should treat this as "capture has already been torn down" and update
   *  its UI accordingly; the mic/audio context are released automatically,
   *  the caller doesn't need to call stopCapture() itself. */
  onConnectionLost(handler: () => void): void {
    this.onConnectionLostCallback = handler;
  }

  /**
   * Requests mic access, opens the Deepgram proxy connection, and starts
   * streaming audio frames. Resolves once both the mic and the Deepgram
   * socket are ready; rejects with an `OnboardingAudioCaptureError` on any
   * failure (permission denied, no input device, proxy unreachable) so the
   * caller can degrade to text-only instead of getting stuck.
   */
  async startCapture(onTranscription: (result: OnboardingTranscriptionResult) => void): Promise<void> {
    if (this.capturing) {
      return;
    }

    this.manualStop = false;
    this.onTranscriptionCallback = onTranscription;

    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: { ideal: 16000 },
          channelCount: 1,
        },
      });
    } catch (err) {
      this.onTranscriptionCallback = null;
      throw this.toCaptureError(err);
    }

    if (this.mediaStream.getAudioTracks().length === 0) {
      this.stopCapture();
      throw new OnboardingAudioCaptureError('no-device', 'No microphone input was found.');
    }

    try {
      this.audioContext = new AudioContext();
      const source = this.audioContext.createMediaStreamSource(this.mediaStream);
      // 4096-sample buffer, mono in/out — matches the frontend single-device
      // path; ScriptProcessorNode is deprecated in favor of AudioWorklet but
      // is what the existing precedent uses and keeps this port simple.
      const processor = this.audioContext.createScriptProcessor(4096, 1, 1);
      this.processor = processor;

      await this.connectDeepgram();

      processor.onaudioprocess = (event: AudioProcessingEvent) => {
        if (!this.capturing || !this.deepgramSocket || this.deepgramSocket.readyState !== WebSocket.OPEN) {
          return;
        }
        const audioData = event.inputBuffer.getChannelData(0);
        const int16Data = convertFloat32ToInt16(audioData);
        this.deepgramSocket.send(int16Data.buffer);
      };

      source.connect(processor);
      processor.connect(this.audioContext.destination);

      this.capturing = true;
    } catch (err) {
      this.stopCapture();
      throw err instanceof OnboardingAudioCaptureError
        ? err
        : new OnboardingAudioCaptureError('connection-failed', 'Could not connect to the transcription service.');
    }
  }

  private async connectDeepgram(): Promise<void> {
    const token = await fetchAudioSocketToken();
    if (!token) {
      throw new OnboardingAudioCaptureError('connection-failed', SIGNED_OUT_MESSAGE);
    }
    return new Promise((resolve, reject) => {
      // Any non-'default' id works — deepgram_proxy_service only uses it to
      // scope logging/subscriber routing, neither of which applies to a
      // single interactive caller with no downstream subscriber. Generated
      // per capture rather than reusing the onboarding session id so this
      // service stays independent of onboardingWebSocketService.
      const roomId = `onboarding-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const sampleRate = this.audioContext?.sampleRate || 48000;
      const url = buildDictationProxyUrl(config.websocket.deepgramUrl, roomId, sampleRate, token);

      let settled = false;
      const socket = new WebSocket(url);
      this.deepgramSocket = socket;

      const timeoutId = setTimeout(() => {
        if (settled) return;
        settled = true;
        reject(new OnboardingAudioCaptureError('connection-failed', 'Timed out connecting to the transcription service.'));
      }, DEEPGRAM_CONNECT_TIMEOUT_MS);

      socket.onopen = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        resolve();
      };

      socket.onerror = () => {
        if (settled) {
          return;
        }
        settled = true;
        clearTimeout(timeoutId);
        reject(new OnboardingAudioCaptureError('connection-failed', 'Could not connect to the transcription service.'));
      };

      socket.onmessage = (event) => {
        const result = parseOnboardingTranscriptionMessage(event.data);
        if (result) {
          this.onTranscriptionCallback?.(result);
        }
      };

      socket.onclose = () => {
        clearTimeout(timeoutId);
        // No reconnect in v1: a mid-capture drop just stops delivering
        // further results. The user's manual "stop" still sends whatever
        // was accumulated so far, so this degrades gracefully rather than
        // silently losing the whole answer.
        //
        // An unexpected drop (network blip, proxy restart) must still fully
        // release the mic/AudioContext — not just flip `capturing` to false
        // — or the hardware stays hot with nothing consuming it. A
        // deliberate stopCapture() already closed this socket itself and
        // sets manualStop first, so this branch only fires for a genuine
        // surprise close.
        if (!this.manualStop) {
          this.stopCapture();
          this.onConnectionLostCallback?.();
        }
      };
    });
  }

  private toCaptureError(err: unknown): OnboardingAudioCaptureError {
    if (err instanceof OnboardingAudioCaptureError) {
      return err;
    }
    const name = err instanceof DOMException ? err.name : undefined;
    if (name === 'NotAllowedError' || name === 'SecurityError') {
      return new OnboardingAudioCaptureError('permission-denied', 'Microphone access was denied.');
    }
    if (name === 'NotFoundError' || name === 'OverconstrainedError') {
      return new OnboardingAudioCaptureError('no-device', 'No microphone was found.');
    }
    const message = err instanceof Error ? err.message : String(err);
    return new OnboardingAudioCaptureError('unknown', message || 'Could not start the microphone.');
  }

  /** Stops mic capture and tears down the Deepgram connection. Safe to call
   *  even if nothing is currently capturing. */
  stopCapture(): void {
    this.manualStop = true;
    this.capturing = false;

    if (this.processor) {
      this.processor.onaudioprocess = null;
      this.processor.disconnect();
      this.processor = null;
    }

    if (this.audioContext) {
      this.audioContext.close().catch(() => {
        // Already closed / closing — nothing to do.
      });
      this.audioContext = null;
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }

    if (this.deepgramSocket) {
      this.deepgramSocket.close();
      this.deepgramSocket = null;
    }

    this.onTranscriptionCallback = null;
  }

  isCapturing(): boolean {
    return this.capturing;
  }
}

export const onboardingAudioCaptureService = new OnboardingAudioCaptureService();
